/// <reference types="vite-plugin-svgr/client" />
import { GlobalMessages } from '@shared/localization/messages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';

import DisketteIcon from '@/assets/diskette-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import { ModalStateless } from '@/components/ModalStateless';
import { CustomDirectCostCreationModal } from '@/components/molecules/CustomDirectCostCreationModal';
import Search from '@/components/molecules/Search';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import type { OmitSafely } from '@/types';
import { generateTemporaryId, isTemporaryId } from '@/utils/generators';
import { sortCostTypes } from '@/utils/sorting';

import type { CostEstimationItemsGroupedTableProps } from '../../CostEstimationItemsGroupedTables';
import CostEstimationItemsGroupedTables from '../../CostEstimationItemsGroupedTables';
import { mapFromCostEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type {
  CostEstimationItemDetail,
  CostEstimationItemEditModel,
  CostEstimationItemsEditFormModel,
  CostEstimationItemsFormModelRow,
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
  EstimationItemIsIncludedActionPromiseCallback,
} from '../../model';
import { MainEstimationItemDetail } from './MainEstimationItemDetail';
import { directCostsMessageDefinitions } from './messages';

type EstimateItemDetailModalProps = OmitSafely<
  CostEstimationItemsGroupedTableProps,
  'control' | 'onIncludeClicked' | 'searchText'
> & {
  open: boolean;
  onClose: () => void;
  onSubmit: (formModel: CostEstimationItemsEditFormModel) => Promise<void>;
  estimationItem: CostEstimationItemDetail | undefined;
  onAssumptionsClicked: EstimationItemActionCallback;
  onCommentsClicked: EstimationItemActionCallback;
  exportProps: OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>;
  keepDirtyValues: boolean;
};

const EstimationItemDetailModal: React.FC<EstimateItemDetailModalProps> = ({
  open,
  onClose,
  onSubmit,
  estimationItem,
  estimationItems,
  onDetailsClicked,
  onAssumptionsClicked,
  onCommentsClicked,
  isReadOnly,
  estimationModel,
  exportProps,
  keepDirtyValues,
  ...remainingTableProps
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(directCostsMessageDefinitions);
  const [searchText, setSearchText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    estimationGroupIdForCustomEstimationItemCreationModal,
    setEstimationGroupIdForCustomEstimationItemCreationModal,
  ] = useState<string | null>();

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => {
      if (estimationItem) {
        void estimationExporter(format, estimationItem.id);
      }
    },
    [estimationExporter, estimationItem],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const formModelInitial = useMemo(() => {
    if (!estimationItem) {
      return undefined;
    }
    const mainEstimationItem: CostEstimationItemEditModel = {
      id: estimationItem.id,
      name: estimationItem.name,
      description: estimationItem.description,
      isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    };
    const subItems = estimationItems.map((x) =>
      mapFromCostEstimationItemsTableRecord(x, estimationModel),
    );
    return {
      mainEstimationItem,
      estimationItems: subItems,
    };
  }, [estimationItem, estimationItems, estimationModel]);

  const { reset, watch, handleSubmit, control, setValue, getValues } =
    useForm<CostEstimationItemsEditFormModel>({ defaultValues: formModelInitial });
  const { append, remove } = useFieldArray({
    control,
    name: 'estimationItems',
    keyName: '_hrf_id',
  });

  const formModel = watch();

  useEffect(() => {
    if (!isSubmitting) {
      reset(formModelInitial, { keepDirtyValues });
    }
  }, [reset, formModelInitial, isSubmitting, keepDirtyValues]);

  const onIncludeClicked: EstimationItemIsIncludedActionPromiseCallback = (
    estimationItemId,
    isIncluded,
  ) => {
    // excluding or deleting
    if (isTemporaryId(estimationItemId)) {
      // if it's just temporarily added item but not saved yet => remove it straight away
      const formModelItemIndex = formModel.estimationItems.findIndex(
        (x) => x.id === estimationItemId,
      );
      remove(formModelItemIndex);
      setValue('estimationItems', getValues('estimationItems'));
      return Promise.reject(new Error('Item removed'));
    }
    if (!isIncluded) {
      const estimItem = estimationItems.find((x) => x.id === estimationItemId);
      if (!estimItem) {
        throw new Error('Estimation item not found.');
      }
      // custom estimation item => display delete confirmation modal
      if (!estimItem.estimationModelItemId) {
        setDelete(watch('estimationItems'), estimationItemId, setValue);
        return Promise.resolve();
      }
      // omit straight away
      setOmit(watch('estimationItems'), estimationItemId, setValue);
      return Promise.resolve();
    }
    return Promise.resolve();
  };

  const onAddEstimationItemClicked: EstimationGroupActionCallback = (estimationGroupId) => {
    return setEstimationGroupIdForCustomEstimationItemCreationModal(
      estimationGroupId ?? estimationModel.itemGroups[0].id,
    );
  };

  const innerOnSubmit = useCallback(
    (model: CostEstimationItemsEditFormModel) => {
      setIsSubmitting(true);
      return onSubmit(model).finally(() => {
        setIsSubmitting(false);
      });
    },
    [setIsSubmitting, onSubmit],
  );

  return (
    <ModalStateless title={messages.title()} open={open} onClose={onClose} withoutButtons>
      {estimationItem && formModel && (
        <>
          <form onSubmit={handleSubmit(innerOnSubmit)}>
            <MainEstimationItemDetail
              control={control}
              estimationItem={estimationItem}
              estimationModel={estimationModel}
              isReadOnly={isReadOnly}
            />
            <div className='my-4 flex grid grid-cols-2 gap-4 sm:grid-cols-4'>
              <Button
                className='flex-1'
                variant='secondary'
                outlined
                onClick={() => onAssumptionsClicked(estimationItem.id)}
              >
                {messages.assumptionsButton()}
              </Button>
              <Button
                className='flex-1'
                variant='secondary'
                outlined
                onClick={() => onCommentsClicked(estimationItem.id)}
              >
                {messages.commentsButton()}
              </Button>
              <div className='hidden sm:block' />
              {isReadOnly ? (
                <div className='hidden sm:block' />
              ) : (
                <Button
                  className='flex-1'
                  type='submit'
                  disabled={isSubmitting}
                  isProcessing={isSubmitting}
                  variant='warning'
                  IconComponentLeft={DisketteIcon}
                >
                  {messages.overrideOriginalEstimateButton()}
                </Button>
              )}
            </div>
            <hr className='my-8' />
            <h2>{messages.subItemsTitle()}</h2>
            <div className='mt-4 flex justify-end gap-2 rounded-xl'>
              <Search handleOnChange={setSearchText} />
              <ExportButtonsSelector
                exportConfiguration={exportConfiguration}
                onExportButtonClick={handleExportButtonClick}
              />
            </div>

            <CostEstimationItemsGroupedTables
              control={control}
              estimationItems={estimationItems}
              isReadOnly={isReadOnly}
              onAssumptionsClicked={onAssumptionsClicked}
              onCommentsClicked={onCommentsClicked}
              onDetailsClicked={onDetailsClicked}
              estimationModel={estimationModel}
              onAddEstimationItemClicked={onAddEstimationItemClicked}
              onIncludeClicked={onIncludeClicked}
              searchText={searchText}
              {...remainingTableProps}
            />
            <div className='my-4 flex justify-end space-x-4'>
              <Button variant='secondary' outlined onClick={onClose}>
                {globalMessages.cancel()}
              </Button>
              {!isReadOnly && (
                <Button
                  type='submit'
                  variant='warning'
                  disabled={isSubmitting}
                  isProcessing={isSubmitting}
                  IconComponentLeft={DisketteIcon}
                >
                  {messages.overrideOriginalEstimateButton()}
                </Button>
              )}
            </div>
          </form>
          <CustomDirectCostCreationModal
            estimationModel={estimationModel}
            defaultGroupId={estimationGroupIdForCustomEstimationItemCreationModal ?? undefined}
            open={estimationGroupIdForCustomEstimationItemCreationModal !== undefined}
            onClose={() => setEstimationGroupIdForCustomEstimationItemCreationModal(undefined)}
            onSave={(customEstimationItemCreationFormModel) => {
              if (!customEstimationItemCreationFormModel.groupId) {
                throw new Error('Group must be chosen');
              }
              append({
                id: generateTemporaryId(),
                name: customEstimationItemCreationFormModel.name,
                description: customEstimationItemCreationFormModel.description,
                isIncluded: true,
                // Same id the form model was built from (formModelInitial), without asserting
                // on a watch() value that is not populated until the first reset.
                parentId: estimationItem.id,
                groupId: customEstimationItemCreationFormModel.groupId,
                omitReason: null,
                shouldDelete: false,
                estimationItemCosts: sortCostTypes(
                  estimationModel.costTypes,
                  estimationModel.costTypeGroups,
                ).map((costType) => ({
                  costTypeId: costType.id,
                  price: null,
                })),
              });
              setEstimationGroupIdForCustomEstimationItemCreationModal(undefined);
            }}
          />
        </>
      )}
    </ModalStateless>
  );
};

function setOmit(
  formModelEstimationItems: CostEstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<CostEstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}

function setDelete(
  formModelEstimationItems: CostEstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<CostEstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.shouldDelete`, true);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}

export default EstimationItemDetailModal;
